import React, { useState } from 'react';
import Page from '../layout/Page';
import { addSuffix, birthday, formatPhone, generateId, getLocal, months, setLocal, shortHandDate, shortHandDateTime, statusColors } from '../utility/functions';
import Dot from '../Dot';
import Table from '../table/Table';
import HamQuest from '../api/HamQuestGiver';
import { QuestRoute } from '../api/QuestRoutes';
import LinkTo from '../forms/LinkTo';
import Row from '../layout/Row';
import Button from '../forms/Button';
import Icon from '../forms/Icon';
import GetState from '../GetState';
import { useGlobalState } from '../stateProvider/StateProvider';
import AddNewCustomer from '../processors/AddNewCustomer';
import { IsAuthorized, userRoles } from '../RoleProtected';
import { accountTypes } from './user';
import TargetedNotification from '../processors/TargetedNotification';
import Users from './expressPass/Users';
import ToggleSwitch from '../forms/ToggleSwitch';
import AddNewQuickSignUp from '../processors/AddNewQuickSignUp';
import NoteToolTip from '../NoteToolTip';
import ModalFactory from '../Modal';
import ManagementRow from '../table/ManagementRow';
import MediaLookUp from './MediaLookUp';
import UserMedia from '../processors/UserMedias';
import UserNotes from '../processors/UserNote';

const Customers = ({ ...props }) => {
    const {
        state: {
            states,
        },
    } = useGlobalState();

    const expressPassKey = 'showEP'

    const [showEP, setShowEP] = useState(getLocal(expressPassKey) || window.location.hash === '#' + expressPassKey);

    const toggleCustomers = () => {
        setLocal(expressPassKey, !showEP);
        setShowEP(!showEP);
    }

    const types = {
        LP: 'LicensePlate',
        RF: 'RFID',
        App: 'AppId',
    };

    const isAuthorized = IsAuthorized(userRoles.viewCustomers);

    const year = new Date().getFullYear() - 2000;
    const month = new Date().getMonth() + 1;

    const cardStatusType = {
        expired: 'expired',
        expiring: 'expiring',
        active: 'active'
    }

    const [customers, _, setCustomers] = GetState(QuestRoute.users, {
        callback: (e) => {
            e.optInToMarketing = e.optInToMarketing  == true ? 'Yes' : 'No';
            e.birthDate = birthday(e.birthdayMonth, e.birthdayDay);
            if (e.birthdayMonth && e.birthdayDay) {
                const month = e.birthdayMonth;
                const day = e.birthdayDay;
                const monthPadded = month.toString().padStart(2, '0');
                const dayPadded = day.toString().padStart(2, '0');
                e.birthDay = [
                    birthday(month, day),
                    `${month}/${day}`,
                    `${monthPadded}/${dayPadded}`,
                    `${monthPadded}/${day}`,
                    `${month}/${dayPadded}`,
                    `${month}-${day}`,
                    `${monthPadded}-${dayPadded}`,
                    `${monthPadded}-${day}`,
                    `${month}-${dayPadded}`,
                    `${months[month - 1]} ${addSuffix(day, 'plain')}`
                ].join(',');
            }
            if (e.userStatus === 'Deleted') e.fullName = 'Deleted User';
            e.awardName = e.awardName || 'None';
            e.clubStatus = e.clubStatus || 'Inactive';
            e.mediaKey = e.loyaltyMedias?.filter(f => f.mediaType !== types.App).map(f => f.mediaId).join(',') || '';
            e.cellPhoneNumber = e.cellPhoneNumber?.replace(/[^0-9]/g, '') ?? 0;

            if (!e.expirationMonth && !e.expirationYear) return

            const eMonth = e.expirationMonth;
            const eYear = e.expirationYear;
            e.cardExpiration = `${eMonth.toString().padStart(2, '0')}/${eYear}`;

            const yearDifference = eYear - year;
            const monthDifference = eMonth - month + yearDifference * 12;

            if (yearDifference < 0 || (yearDifference === 0 && eMonth < month)) {
                e.cardStatus = cardStatusType.expired;
            } else if (monthDifference >= 0 && monthDifference <= 3) {
                e.cardStatus = cardStatusType.expiring;
            } else {
                e.cardStatus = cardStatusType.active;
            }
        }
    })

    const resendSignup = id => {
        let haveSent = getLocal('ResendSignup');
        if (!haveSent) {
            console.log('Setting ResendSignup');
            setLocal('ResendSignup', {})
            haveSent = {}
        }
        const now = new Date();

        if (haveSent[id]) {
            const lastSent = new Date(haveSent[id]);
            const differenceMs = now - lastSent;
            const differenceDays = differenceMs / (1000 * 60 * 60 * 24);
            const isOneWeekAfterLastSent = differenceDays >= 7;

            if (!isOneWeekAfterLastSent) {
                ModalFactory.show({
                    title: 'Please Wait',
                    body: (<span>Please wait 1 week before sending another text. Next available {shortHandDate(lastSent.setDate(lastSent.getDate() + 7))}</span>),
                    type: 'error',
                })
                return;
            }
        }

        ModalFactory.confirmAndProcess({
            confirmation: {
                title: `Resend Signup Confirmation?`,
                body: (<span>Are you sure you want to send a confirmation text?</span>),
                type: 'warning',
            },
            promise: {
                title: 'Sending',
                body: `Sending, please wait.`,
                callback: () => HamQuest.get(QuestRoute.user.resendSignup(id))
            },
            confirmed: {
                title: 'Success',
                body: `Text has been sent.`,
                type: 'success'
            },
            finally: () => {
                haveSent[id] = now;
                setLocal('ResendSignup', haveSent);
            }
        })
    }

    const tableConfig = {
        tableId: 'customer',
        data: customers,
        sticky: true,
        colSpacing: [10, 12, 10, 8, 12, 8, 10, 10, 10, 10],
        controls: [
            <Button
                roles={userRoles.editCustomers}
                onClick={() => {
                    const id = generateId();
                    ModalFactory.show({
                        id,
                        title: `New Customer`,
                        body: <AddNewCustomer modalId={id} />,
                        buttons: [
                            <Button
                                label='Quick Signup'
                                onClick={() => ModalFactory.update({ id, title: `Quick Signup`, body: <AddNewQuickSignUp />, })}
                            />
                        ]
                    })
                }}
                label='New Customer'
            />,
            <Button
                onClick={() => {
                    const id = generateId();
                    ModalFactory.show(window.customerTableData.length ? {
                        id,
                        title: `Send Notification`,
                        body: <TargetedNotification modalId={id} users={window.customerTableData} />
                    } : {
                        id,
                        title: 'No Users',
                        body: 'No users selected, please have at least one user.',
                        buttons: [<Button to='/mobile#PushNotifications' onClick={() => ModalFactory.close(id)}>Global Notifications</Button>],
                        type: 'error',
                    })
                }}
                label='Send Targeted Push'
            />,
        ],
        headers: [
            {
                label: 'Name',
                propKey: 'fullName',
            }, {
                label: 'Email',
                propKey: 'email',
            }, {
                label: 'Phone Number',
                propKey: 'cellPhoneNumber',
                type: 'number'
            }, {
                label: 'Status',
                propKey: 'userStatus',
                options: true
            }, {
                label: 'Last Login',
                propKey: 'lastLoginDate',
                type: 'dateTime',
                sortBy: true,
                ascending: false
            }, {
                label: 'Date Created',
                propKey: 'createDate',
                type: 'date',
            }, {
                label: 'Wash Club',
                propKey: 'awardName',
                options: true
            }, {
                label: 'Club Status',
                propKey: 'clubStatus',
                options: true
            }, {
                label: 'Birthday',
                propKey: 'birthDay',
            }, {
                label: 'Masked Card',
                propKey: 'maskedCardNumber',
            }, {
                label: 'Billing Day',
                propKey: 'billingDay',
                show: false
            },  {
                label: 'Marketing Opt-In',
                propKey: 'optInToMarketing',
                show: false
            }
            //  {
            //     label: 'Card Expiration',
            //     propKey: 'cardExpiration',
            //     show: false
            // },
        ],
        quickSort: [
            {
                label: 'Active Users',
                propKey: 'userStatus',
                query: 'Active',
                color: '#21D59B',
                defaultSearch: true
            }, {
                label: 'Wash Clubs',
                propKey: 'clubStatus',
                query: 'Active',
                color: '#10BBF3'
            }, {
                label: 'Invalid Users',
                propKey: 'userStatus',
                query: 'NotValidated',
                color: '#93090e'
            }, {
                label: 'Inactive Users',
                propKey: 'userStatus',
                query: 'Inactive',
                color: '#44474e'
            }, {
                label: 'Credit Hold',
                propKey: 'clubStatus',
                query: 'CreditHold',
                color: '#ff4141'
            }, {
                label: 'Parent Account',
                propKey: 'accountType',
                query: accountTypes.parent,
                color: '#6a7dff'
            }, {
                label: 'Child Account',
                propKey: 'accountType',
                query: accountTypes.child,
                color: '#ff6aaf'
            }, {
                label: 'Expiring Card',
                propKey: 'cardStatus',
                query: cardStatusType.expiring,
                color: '#ffa500'
            }, {
                label: 'Expired Card',
                propKey: 'cardStatus',
                query: cardStatusType.expired,
                color: '#bf0d14'
            }, {
                label: 'Marketing Opt-In',
                propKey: 'optInToMarketing',
                query: 'Yes',
                color: '#ee234d'
            }
        ],
        renderFormat: (e) => (
            <ManagementRow
                key={generateId()}
                controls={[
                    <Button
                        roles={userRoles.editProducts}
                        key={generateId()}
                        onClick={() => ModalFactory.show({ title: `${e.firstName}'s Media`, body: <UserMedia userId={e.customerId} states={states} /> })}
                        label='Edit Media'
                    />,
                ]}
            >
                <td>
                    <Row>
                        <NoteToolTip
                            onClick={() => ModalFactory.show({ title: `${e.firstName}'s Notes`, body: <UserNotes customerId={e.customerId} dataSet={customers} updateDataSet={setCustomers} /> })}
                            icon={e.note ? 'pencil' : 'plus'}
                            note={e.note}
                        />
                        {isAuthorized ?
                            <LinkTo customerId={e.userId}>{e.fullName}</LinkTo> : <div>{e.fullName}</div>}
                    </Row>
                </td>
                <td>{e.email}</td>
                <td>
                    <Row>
                        {formatPhone(e.cellPhoneNumber) || '-'}
                        {e.firstName === 'Valued Customer' && (
                            <Icon
                                onClick={() => resendSignup(e.userId)}
                                icon='comment'
                                style={{ cursor: 'pointer' }}
                                title='Resend Signup Text'
                            />
                        )}
                    </Row>
                </td>
                <td>
                    <Row>
                        <Dot color={statusColors[e.userStatus]} />
                        <span>
                            {e.userStatus}
                        </span>
                    </Row>
                </td>
                <td>{shortHandDateTime(e.lastLoginDate)}</td>
                <td>{shortHandDate(e.createDate)}</td>
                <td>{e.awardName}</td>
                <td>
                    <Row>
                        <Dot color={statusColors[e.clubStatus]} />
                        <span>
                            {e.clubStatus} {e.accountType !== accountTypes.standard ? e.accountType === accountTypes.parent ? '(Parent)' : '(Child)' : null}
                        </span>
                    </Row>
                </td>
                <td>{e.birthDate || '-'}</td>
                <td>{e.maskedCardNumber?.replace(/\D/g, '') || '-'}</td>
                <td>{addSuffix(e.billingDay)}</td>
                <td>{e.optInToMarketing}</td>                
                {/* <td>{e.cardExpiration || '-'}</td> */}
            </ManagementRow>
        )
    }

    return (
        <Page
            title='Customers'
            icon='user'
            controls={[
                <ToggleSwitch
                    key='CustyToggleSwitch'
                    options={[
                        {
                            label: 'App Users',
                            value: false
                        }, {
                            label: 'ExpressPass Users',
                            value: true
                        }
                    ]}
                    value={showEP}
                    onClick={toggleCustomers}
                />,
                <Button
                    onClick={() => {
                        const id = generateId();
                        ModalFactory.show({
                            id,
                            title: `Media`,
                            body: <MediaLookUp justTable={true} />,
                        })
                    }}
                    label='Search by Media'
                />
            ]}
        >
            {showEP
                ? <Users />
                : <Table config={tableConfig} />}
        </Page >
    );
}

export default Customers;
